import { Alert, Box, Button, Typography } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloudOffIcon from '@mui/icons-material/CloudOff'
import { DateTime } from 'luxon'
import React, { useEffect, useState } from 'react'
import { NoteDraftEntry, NoteDraftStatus, useNoteDraftEntry } from './noteDraftSession'

const CHIP_SLOT_PX = 28
// A save often takes a few hundred ms. Without a floor the label would flicker on every save.
const MIN_SAVING_MS = 600

const deletionLabel = (deletesOn: Date) => DateTime.fromJSDate(deletesOn).toLocaleString(DateTime.DATE_MED)

export const noteDraftDeletionText = (deletesOn: Date | null): string | null => {
  if (!deletesOn) {
    return null
  }
  return deletesOn.getTime() > Date.now()
    ? `This draft will be deleted on ${deletionLabel(deletesOn)}.`
    : 'This draft is past its 30 days and will be deleted within a day.'
}

// A failed save that is still retrying shows on the chip. These two need the user to act, so they get a banner.
export const noteDraftBannerShown = (status: NoteDraftStatus) => status === 'conflict' || status === 'gone'

const savedAtLabel = (savedAt: Date) => {
  const time = DateTime.fromJSDate(savedAt)
  return time.hasSame(DateTime.now(), 'day')
    ? time.toLocaleString(DateTime.TIME_SIMPLE)
    : time.toLocaleString({ month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

const useShownSaving = (saving: boolean) => {
  const [since, setSince] = useState<number | null>(saving ? Date.now() : null)
  const [shown, setShown] = useState(saving)

  useEffect(() => {
    if (saving) {
      setSince((current) => current ?? Date.now())
      setShown(true)
      return undefined
    }
    if (since === null) {
      setShown(false)
      return undefined
    }
    const timer = window.setTimeout(() => {
      setSince(null)
      setShown(false)
    }, Math.max(0, since + MIN_SAVING_MS - Date.now()))
    return () => window.clearTimeout(timer)
  }, [saving, since])

  return shown
}

const NoteDraftErrorBanner = ({ status }: { status: NoteDraftStatus }) => {
  if (status === 'conflict') {
    return (
      <Alert
        severity="error"
        variant="filled"
        elevation={6}
        action={
          <Button color="inherit" size="small" onClick={() => window.location.reload()}>
            Reload
          </Button>
        }
      >
        This note was changed in another window, so changes here are no longer saved. Reload to continue from the saved
        version. Text typed here since the last save will be lost, so copy anything you want to keep first.
      </Alert>
    )
  }
  return (
    <Alert severity="error" variant="filled" elevation={6}>
      This draft is no longer saved. It was submitted elsewhere or deleted 30 days after it was started. The text below
      is only in this window: submit the note or copy the text before you leave.
    </Alert>
  )
}

export const NoteDraftLoadError = () => (
  <Alert
    severity="error"
    action={
      <Button color="inherit" size="small" onClick={() => window.location.reload()}>
        Reload
      </Button>
    }
  >
    Couldn&apos;t load the draft for this note. Reload to try again.
  </Alert>
)

const NoteDraftStatusChip = ({ entry }: { entry: NoteDraftEntry }) => {
  const saving = useShownSaving(entry.status === 'pending' || entry.changed)

  let label: React.ReactNode = null
  // A retry sets status back to pending, so the failure wins over "Saving…" until a save succeeds.
  if (entry.failing) {
    label = (
      <>
        <CloudOffIcon sx={{ fontSize: 14, mr: 0.5 }} />
        Not saved · retrying
      </>
    )
  } else if (saving) {
    label = 'Saving…'
  } else if (entry.status === 'saved' && entry.savedAt) {
    label = (
      <>
        <CheckIcon sx={{ fontSize: 14, mr: 0.5 }} />
        {`Saved ${savedAtLabel(entry.savedAt)}`}
      </>
    )
  } else if (entry.status === 'idle' && entry.savedAt) {
    label = `Draft from ${savedAtLabel(entry.savedAt)}`
  }

  if (!label) {
    return null
  }

  const deletion = entry.failing ? null : noteDraftDeletionText(entry.deletesOn)
  let ariaLabel = 'Draft saved'
  if (entry.failing) {
    ariaLabel = 'Draft not saved'
  } else if (saving) {
    ariaLabel = 'Saving draft'
  }

  return (
    <Box
      role="status"
      aria-label={ariaLabel}
      sx={{
        px: 1.5,
        py: 0.5,
        borderRadius: 2,
        border: 1,
        borderColor: entry.failing ? 'warning.main' : 'divider',
        bgcolor: 'background.paper',
        boxShadow: 1,
        color: entry.failing ? 'warning.dark' : 'text.secondary',
        textAlign: 'right',
      }}
    >
      <Typography
        variant="caption"
        component="div"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
      >
        {label}
      </Typography>
      {deletion && (
        <Typography variant="caption" component="div">
          {deletion}
        </Typography>
      )}
    </Box>
  )
}

// Sits at the top of the form and sticks to the top of the scrolling area while the form is in view: a quiet save
// status, replaced by a loud banner when saving has stopped and the user has to act. The chip's height is always reserved, so the form
// does not jump when "Saving…" first appears.
export const NoteDraftAlerts = ({ uid, formType }: { uid: string; formType: string }) => {
  const entry = useNoteDraftEntry({ uid, type: formType })
  const failing = noteDraftBannerShown(entry.status)

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar - 1,
        minHeight: CHIP_SLOT_PX,
        mb: 2,
        display: 'flex',
        justifyContent: 'flex-end',
        pointerEvents: failing ? 'auto' : 'none',
      }}
    >
      {failing ? (
        <Box sx={{ width: '100%' }}>
          <NoteDraftErrorBanner status={entry.status} />
        </Box>
      ) : (
        <NoteDraftStatusChip entry={entry} />
      )}
    </Box>
  )
}
